import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiActivity, FiBarChart2, FiTrendingUp, FiRefreshCw, FiLoader } = FiIcons;

const SubscriptionAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch users with plans
      const { data, error: rpcError } = await supabase.rpc('admin_get_users_with_plans');
      
      if (rpcError) throw rpcError;
      
      if (data?.success && data?.users) {
        const users = data.users;
        
        // Calculate analytics
        const planCounts = {
          free: 0,
          starter: 0,
          pro_trader: 0,
          institutional_ai: 0,
          enterprise: 0,
          none: 0
        };

        let totalAnalyses = 0;

        users.forEach(user => {
          const planId = user.subscription_plan_id;
          if (planId === 'free') planCounts.free++;
          else if (planId === 'starter') planCounts.starter++;
          else if (planId === 'pro_trader') planCounts.pro_trader++;
          else if (planId === 'institutional_ai') planCounts.institutional_ai++;
          else if (planId === 'enterprise') planCounts.enterprise++;
          else planCounts.none++;
          
          totalAnalyses += user.analyses_used_this_month || 0;
        });
        
        setAnalytics({
          planCounts,
          totalAnalyses,
          totalUsers: users.length
        });
        
        // Get top 10 users by usage
        const sortedUsers = [...users]
          .sort((a, b) => (b.analyses_used_this_month || 0) - (a.analyses_used_this_month || 0))
          .slice(0, 10);
        
        setTopUsers(sortedUsers);
      }
    } catch (err) {
      console.error('❌ Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <SafeIcon icon={FiLoader} className="animate-spin text-purple-400 text-2xl mr-2" />
        <span className="text-gray-400">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
        <p className="text-red-300">{error}</p>
      </div>
    );
  }

  const planLabels = {
    free: { name: 'Free', color: 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-300' },
    starter: { name: 'Starter', color: 'from-green-500/20 to-emerald-500/20 border-green-500/30 text-green-300' },
    pro_trader: { name: 'Pro Trader', color: 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-300' },
    institutional_ai: { name: 'Institutional AI', color: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-300' },
    enterprise: { name: 'Enterprise', color: 'from-amber-500/20 to-yellow-500/20 border-amber-500/30 text-amber-300' },
    none: { name: 'No Plan', color: 'bg-gray-700/50 border-gray-600 text-gray-400' }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white flex items-center">
          <SafeIcon icon={FiBarChart2} className="mr-2 text-cyan-400" />
          Subscription Analytics
        </h3>
        <button
          onClick={fetchAnalytics}
          disabled={loading}
          className="text-sm bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded-lg flex items-center transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Plan Distribution */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(analytics?.planCounts || {}).map(([planId, count]) => {
          const plan = planLabels[planId];
          return (
            <motion.div
              key={planId}
              whileHover={{ scale: 1.02 }}
              className={`bg-gradient-to-br ${plan.color} rounded-xl p-4 border`}
            >
              <p className="text-xs text-gray-400 uppercase tracking-wide">{plan.name}</p>
              <p className="text-2xl font-bold text-white mt-1">{count}</p>
              <p className="text-xs text-gray-500">
                {analytics?.totalUsers > 0 
                  ? `${((count / analytics.totalUsers) * 100).toFixed(0)}%`
                  : '0%'}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Total Analyses This Month */}
      <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 mr-3">
              <SafeIcon icon={FiActivity} className="text-cyan-400 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total AI Analyses This Month</p>
              <p className="text-2xl font-bold text-white">{analytics?.totalAnalyses || 0}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Avg per User</p>
            <p className="text-lg font-semibold text-cyan-400">
              {analytics?.totalUsers > 0 
                ? (analytics.totalAnalyses / analytics.totalUsers).toFixed(1)
                : '0'}
            </p>
          </div>
        </div>
      </div>

      {/* Top 10 Users by Usage */}
      <div className="bg-gray-900/50 rounded-xl border border-gray-700/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700/50 bg-gray-800/30">
          <h4 className="font-semibold text-white flex items-center">
            <SafeIcon icon={FiTrendingUp} className="mr-2 text-cyan-400" />
            Top 10 Users by AI Usage
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-gray-500 uppercase border-b border-gray-700/50">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-right">Analyses</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No usage data available
                  </td>
                </tr>
              ) : (
                topUsers.map((user, index) => {
                  const plan = planLabels[user.subscription_plan_id] || planLabels.none;
                  return (
                    <tr key={user.user_id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3">
                        <span className="text-white font-mono text-sm">{user.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${plan.color}`}>
                          {plan.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${user.analyses_used_this_month > 0 ? 'text-cyan-400' : 'text-gray-500'}`}>
                          {user.analyses_used_this_month || 0}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAnalytics;
